import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import {
  FLOORSHEET_RANGE_AVAILABLE,
  type FloorsheetDay,
} from '@/features/floorsheet/api/floorsheetService'
import {
  floorsheetBrokerOptionsQueryOptions,
  floorsheetInsightsQueryOptions,
  floorsheetSummaryQueryOptions,
  floorsheetTradesQueryOptions,
} from '@/features/floorsheet/api/queries'
import { calendarDateSchema } from '@/schemas/portfolio'
import { floorsheetQuerySchema } from '@/schemas/floorsheet'
import type { RouterContext } from '@/routes/layouts/root'

/**
 * The URL, here rather than in FloorsheetScreen.tsx because the loader parses it too, and the
 * route's `validateSearch` and the loader must not be able to disagree about which day a URL means.
 *
 * `date` is AD ISO and optional (absent means today). `from`/`to` are the date-range pair, read
 * only while `FLOORSHEET_RANGE_AVAILABLE` is true. A malformed or impossible day falls back to
 * today rather than throwing out of `validateSearch` (an error page over a filter) or reaching
 * mm-be as a 400. An old `?view=` link (the By Broker / By Symbol tabs, dropped by owner ruling 7)
 * is simply ignored: the object strips keys it does not know.
 */
export const floorsheetSearchSchema = floorsheetQuerySchema.extend({
  date: calendarDateSchema.optional().catch(undefined),
  from: calendarDateSchema.optional().catch(undefined),
  to: calendarDateSchema.optional().catch(undefined),
})

/** The day the URL names: a range (when served), one day, or today. A range is read earlier-first. */
export const floorsheetDayOf = (search: {
  date?: string | undefined
  from?: string | undefined
  to?: string | undefined
}): FloorsheetDay => {
  const { date, from, to } = search
  if (FLOORSHEET_RANGE_AVAILABLE && from !== undefined && to !== undefined) {
    return from <= to ? { kind: 'range', from, to } : { kind: 'range', from: to, to: from }
  }
  return date === undefined ? { kind: 'today' } : { kind: 'day', date }
}

/**
 * The `/market/floorsheet` loader.
 *
 * MAIN: the entry URL's page of the trade log — awaited.
 *
 * SECONDARY, started and not awaited, and only on a real navigation (`!preload`): the summary
 * panel, the two cards under the table, and the Buyer / Seller menus' rows. A hover-intent preload
 * of this route must not fire four requests as the cursor merely passes over the nav.
 *
 * Read from `location.search` through the route's own schema, not `loaderDeps`: a dep would make
 * every filter, sort or pager click a new route match — the full-page skeleton on each one.
 *
 * Its own module so a test can mount it under a root of its own.
 */
export async function loadFloorsheet({
  context: { queryClient },
  location,
  preload,
}: {
  context: RouterContext
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  const search = floorsheetSearchSchema.parse(location.search)
  const day = floorsheetDayOf(search)

  if (!preload) {
    prefetchRouteData(queryClient, floorsheetSummaryQueryOptions(day))
    prefetchRouteData(queryClient, floorsheetBrokerOptionsQueryOptions())
    if (day.kind === 'today') prefetchRouteData(queryClient, floorsheetInsightsQueryOptions(day))
  }
  await ensureRouteData(queryClient, floorsheetTradesQueryOptions(search, day))
}
